import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'How to Write a Late Payment Follow-Up Email (+ Templates)',
      'description': 'Four ready-to-send email templates for chasing unpaid invoices — from a polite first reminder to a firm final notice.',
      'url': 'https://www.invilic.com/blog/late-payment-follow-up-email',
      'datePublished': '2025-05-20',
      'dateModified': '2025-05-20',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'How long should you wait before chasing an unpaid invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Send a friendly reminder 1–2 days after the due date passes — not before, as the client may still be processing the payment. If there is no response within 7 days, send a firmer follow-up. Escalate to a final notice after 14–30 days. For clients you have worked with before, a quick WhatsApp or phone call alongside the email can speed things up.' },
        },
        {
          '@type': 'Question',
          'name': 'Can you charge interest on late invoices?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, in most countries you can charge late payment interest if it is stated in your invoice terms or a prior written agreement. In the UK, the Late Payment of Commercial Debts Act allows you to charge 8% over the Bank of England base rate. In India, you can agree to interest terms in your contract. In the US, the rules vary by state. Always state your late payment terms clearly on the original invoice.' },
        },
        {
          '@type': 'Question',
          'name': 'What is the legal process for unpaid invoices?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'After exhausting email reminders, your options are: (1) small claims court for smaller amounts (typically under £10,000 in the UK, $10,000 in the US); (2) a solicitor\'s or lawyer\'s letter, which often prompts payment; (3) a debt collection agency (they take a percentage); (4) mediation services. Keep all invoice records, emails, and communications as evidence.' },
        },
        {
          '@type': 'Question',
          'name': 'How do you politely ask a client for payment without damaging the relationship?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Keep your first one or two reminders warm and assume the non-payment is an oversight. Use phrases like "Just wanted to follow up on invoice #123" rather than "You have failed to pay." Reference the specific invoice number, amount, and original due date so the client can act immediately. Reserve firmer language for the third or fourth follow-up when it is clear the delay is deliberate.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const templates = [
  {
    label: 'Template 1 — First Reminder (1–2 days after due date)',
    tag: 'Friendly',
    tagColor: 'text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30',
    subject: 'Invoice #[INV-001] — Payment Reminder',
    body: `Hi [Client Name],

I hope you're well. I wanted to follow up on Invoice #[INV-001] for [amount], which was due on [due date].

If you've already sent the payment, please disregard this message — it may just be crossing in transit.

If not, could you let me know when I can expect to receive it?

Payment details are on the original invoice, but happy to resend if helpful.

Thank you,
[Your Name]`,
  },
  {
    label: 'Template 2 — Second Reminder (7 days after due date)',
    tag: 'Firm but polite',
    tagColor: 'text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30',
    subject: 'Invoice #[INV-001] — Now 7 Days Overdue',
    body: `Hi [Client Name],

I'm following up again on Invoice #[INV-001] for [amount], which was due on [due date] and is now 7 days overdue.

Could you please confirm when payment will be processed? If there is any issue with the invoice or if you need the bank details resent, I'm happy to help.

If payment has already been made, please share the transfer confirmation so I can mark this as resolved.

Thank you for your prompt attention to this.

[Your Name]`,
  },
  {
    label: 'Template 3 — Final Notice (14–30 days after due date)',
    tag: 'Firm',
    tagColor: 'text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30',
    subject: 'Invoice #[INV-001] — Urgent: Payment Required',
    body: `Hi [Client Name],

This is my third reminder regarding Invoice #[INV-001] for [amount], now [X] days overdue.

As this invoice remains unpaid, I need to request payment within the next 7 days to avoid having to escalate this matter further. Please note that as per my payment terms, late interest may apply from the original due date.

If you are experiencing difficulties, please contact me so we can discuss a resolution.

I would prefer to resolve this directly and continue our working relationship. Please reply or make payment to the details on the invoice.

[Your Name]`,
  },
  {
    label: 'Template 4 — Final Demand (30+ days, pre-legal)',
    tag: 'Formal',
    tagColor: 'text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30',
    subject: 'FINAL DEMAND: Invoice #[INV-001] — [Amount] Outstanding',
    body: `Dear [Client Name],

This letter serves as formal notice that Invoice #[INV-001] for [amount], issued on [invoice date] with a due date of [due date], remains unpaid after [X] days.

Despite multiple reminders, no payment has been received and no satisfactory response has been provided.

Unless full payment is received within 7 days of this notice, I will have no alternative but to pursue this matter through the appropriate legal channels, including but not limited to a small claims court filing and/or engagement of a debt collection service.

This is not a decision I take lightly, and I would prefer to resolve this matter directly. Please make payment immediately or contact me to discuss.

Regards,
[Your Full Name]
[Your Business Name]
[Your Contact Details]`,
  },
];

export default function LatePaymentEmailPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="How to Write a Late Payment Follow-Up Email (+ 4 Templates) | Invilic Blog"
        description="Four ready-to-send email templates for chasing unpaid invoices — from a friendly first reminder to a firm final demand. Copy and use."
        canonical="https://www.invilic.com/blog/late-payment-follow-up-email"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">Getting Paid</span>
          </div>
          <span className="inline-block bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">Getting Paid</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            How to Write a Late Payment Follow-Up Email (+ 4 Templates)
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">May 20, 2025 · 5 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            Send your first payment reminder 1–2 days after the due date, keep the tone friendly, and reference the specific invoice number and amount so the client can act immediately. Escalate to firmer language only after a second non-response. Below are four templates you can copy and send today.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">When to Send Each Reminder</h2>
          <div className="space-y-3 mb-8">
            {[
              { timing: '1–2 days after due date', action: 'First reminder — friendly, assume it is an oversight' },
              { timing: '7 days after due date', action: 'Second reminder — firmer, ask for confirmation' },
              { timing: '14–21 days after due date', action: 'Third reminder — mention late interest, escalation possible' },
              { timing: '30+ days after due date', action: 'Final demand — reference legal action, set a hard deadline' },
            ].map(({ timing, action }) => (
              <div key={timing} className="flex gap-4 p-4 border border-gray-100 dark:border-gray-800 rounded-xl">
                <div className="text-xs font-bold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 px-2.5 py-1 rounded-full h-fit whitespace-nowrap">{timing}</div>
                <p className="text-sm">{action}</p>
              </div>
            ))}
          </div>

          <BlogCTA
            text="Always include payment instructions clearly on your invoice so clients have no excuse. Create yours free on Invilic."
            to="/create/international"
            btnText="Create Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">The 4 Email Templates</h2>
          <div className="space-y-6">
            {templates.map(({ label, tag, tagColor, subject, body }) => (
              <div key={label} className="border border-gray-100 dark:border-gray-800 rounded-2xl overflow-hidden">
                <div className="px-5 py-4 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between gap-4">
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">{label}</p>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${tagColor}`}>{tag}</span>
                </div>
                <div className="p-5">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">SUBJECT</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-4 bg-gray-50 dark:bg-gray-900/30 px-3 py-2 rounded-lg">{subject}</p>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">BODY</p>
                  <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-sans leading-relaxed bg-gray-50 dark:bg-gray-900/30 px-4 py-4 rounded-xl">{body}</pre>
                </div>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Tips That Actually Work</h2>
          <ul className="space-y-3 mb-8">
            {[
              'Always reference the specific invoice number and amount — make it zero-effort for the client to act.',
              'Attach the original invoice to every reminder email — some clients genuinely lose the first one.',
              'Add payment terms and late interest clauses to your invoice upfront — it sets expectations before work starts.',
              'A short phone call or WhatsApp message alongside your email often resolves late payments in minutes.',
              'For repeat offenders, consider requiring a 50% deposit upfront before starting future projects.',
            ].map(tip => (
              <li key={tip} className="flex items-start gap-2 text-sm">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                {tip}
              </li>
            ))}
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Create a professional invoice with clear payment terms and due dates — fewer late payments start with a better invoice."
            to="/create/freelancer"
            btnText="Create Invoice Free"
          />

        </article>
        <RelatedArticles current="/blog/late-payment-follow-up-email" />
      </main>
    </div>
  );
}
